// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    InvoicePaymentHistoryCollectionModel,
    InvoicePaymentHistoryModel,
} from "@eisinc/App/Services/InvoicePaymentHistoryService";
import { GetInvoicePaymentHistoryApiParameter } from "@eisinc/App/Store/Reducers/xInvoicePaymentHistoryListReducer";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { DataViewState, setDataViewLoaded, setDataViewLoading } from "@insite/client-framework/Store/Data/DataState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export interface InvoicePaymentHistoryState extends DataViewState<InvoicePaymentHistoryModel> {
    readonly idByOrderSku: Dictionary<string>;
    failedToLoadInvoicePaymentHistory?: boolean;
}

const initialState: InvoicePaymentHistoryState = {
    isLoading: {},
    byId: {},
    idByOrderSku: {},
    dataViews: {},
    errorStatusCodeById: {},
};

const reducer = {
    "Data/InvoicePaymentHistory/BeginLoadInvoicePaymentHistory": (
        draft: Draft<InvoicePaymentHistoryState>,
        action: { parameter: GetInvoicePaymentHistoryApiParameter },
    ) => {
        setDataViewLoading(draft, action.parameter);
    },

    "Data/InvoicePaymentHistory/CompleteLoadInvoicePaymentHistory": (
        draft: Draft<InvoicePaymentHistoryState>,
        action: { parameter: GetInvoicePaymentHistoryApiParameter; collection: InvoicePaymentHistoryCollectionModel },
    ) => {
        const collectionWithIds = {
            ...action.collection,
            clearedDocumentList: action.collection.clearedDocumentList!.map(item => ({
                ...item,
                id: item.accountingDoc,
            })),
        };

        setDataViewLoaded(draft, action.parameter, collectionWithIds, collection => collection.clearedDocumentList!);

        if (draft.failedToLoadInvoicePaymentHistory) {
            draft.failedToLoadInvoicePaymentHistory = false;
        }
    },

    "Data/InvoicePaymentHistory/FailedToLoadInvoicePaymentHistory": (draft: Draft<InvoicePaymentHistoryState>) => {
        draft.failedToLoadInvoicePaymentHistory = true;
    },

    "Data/InvoicePaymentHistory/Reset": () => {
        return initialState;
    },
};

const xInvoicePaymentHistoryState = createTypedReducerWithImmer(initialState, reducer);

(reducers as any).xInvoicePaymentHistoryState = xInvoicePaymentHistoryState;

export type xInvoicePaymentHistoryState = {
    xInvoicePaymentHistoryState: InvoicePaymentHistoryState;
};

type xInvoicePaymentHistoryStateReducers = {};

export type xInvoicePaymentHistoryStateActions =
    | Parameters<xInvoicePaymentHistoryStateReducers[keyof xInvoicePaymentHistoryStateReducers]>[1]
    | AnyAction;
